import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApiResponse, ApiError } from '../types';
import { getEnv } from '../config/env';

export interface HttpClientConfig {
  baseURL: string;
  timeout?: number;
  defaultHeaders?: Record<string, string>;
}

export class HttpClient {
  private instance: AxiosInstance;
  private getAccessToken?: () => string | null;

  constructor(config: HttpClientConfig) {
    this.instance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
        ...config.defaultHeaders,
      },
    });

    this.setupInterceptors();
  }

  setAccessTokenGetter(getter: () => string | null) {
    this.getAccessToken = getter;
  }

  private setupInterceptors() {
    // Request interceptor to add auth token
    this.instance.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken?.();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          // Preserve the original error but enhance it with our ApiError structure
          const apiError: ApiError = {
            errorCode: error.response.data?.errorCode || 'UNKNOWN_ERROR',
            message: error.response.data?.message || 'An unexpected error occurred',
            details: error.response.data?.details,
          };
          
          // Keep the original error but add our structured apiError
          error.apiError = apiError;
          return Promise.reject(error);
        }
        
        if (error.request) {
          const networkError: ApiError = {
            errorCode: 'NETWORK_ERROR',
            message: 'Network error - please check your connection',
          };
          error.apiError = networkError;
          return Promise.reject(error);
        }
        
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.delete<T>(url, config);
    return response.data;
  }

  async getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.instance.get(url, {
      ...config,
      responseType: 'blob',
    });
    return response.data;
  }

  getAxiosInstance(): AxiosInstance {
    return this.instance;
  }
}

// Create a default instance
const httpClient = new HttpClient({
  baseURL: getEnv('REACT_APP_API_BASE_URL', 'http://localhost:5000'),
  timeout: 30000,
});

export default httpClient;
